import React, { useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";

import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { GET_CONNECT_ACCOUNT, GetConnectAccountResponse } from "./queries";

import ConnectSetup from "./connectSetup";
import AccountStatus from "./accountStatus";
import Summary from "./summary";
import Transactions from "./transactions";
import Payouts from "./payouts";
import PaymentLinks from "./paymentLinks";

const Payments: React.FC = () => {
  const { section } = useParams<{ section?: string }>();
  const location = useLocation();

  const { data, loading, refetch } = useQuery<GetConnectAccountResponse>(
    GET_CONNECT_ACCOUNT,
    { fetchPolicy: "cache-and-network" },
  );

  // Handle return from Stripe onboarding
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");
    if (status === "success") {
      toast.success(
        lang(
          "Cuenta conectada correctamente",
          "Account connected successfully",
        ),
      );
      refetch();
    } else if (status === "refresh") {
      toast.info(
        lang(
          "Continua con la verificacion",
          "Please continue with verification",
        ),
      );
      refetch();
    }
    // eslint-disable-next-line
  }, [location.search]);

  if (loading && !data) {
    return <LoaderDots fullSize={true} />;
  }

  const account = data?.getConnectAccount;

  // No Connect account → show setup
  if (!account) {
    return <ConnectSetup onComplete={refetch} />;
  }

  // Onboarding not completed
  if (account.status === "onboarding") {
    return (
      <main className="sales_page">
        <div className="container">
          <AccountStatus account={account} onUnlinked={refetch} />
          <div className="text-center py-5">
            <i className="fas fa-clock fa-3x" style={{ color: "#FFBB28" }} />
            <h4 className="mt-3">
              {lang(
                "Completa la verificacion de tu cuenta",
                "Complete your account verification",
              )}
            </h4>
            <p className="text-muted">
              {lang(
                "Haz clic en 'Completar verificacion' para continuar con el proceso.",
                "Click 'Complete verification' to continue with the process.",
              )}
            </p>
          </div>
        </div>
      </main>
    );
  }

  // Active account → tabbed dashboard
  function renderView() {
    switch (section) {
      case "transactions":
        return <Transactions />;
      case "payouts":
        return <Payouts />;
      case "links":
        return <PaymentLinks />;
      default:
        return <Summary />;
    }
  }

  return (
    <main className="sales_page">
      <div className="container">
        <AccountStatus account={account} onUnlinked={refetch} />

        {/* Tab nav — reuse sales-nav pattern */}
        <nav className="sales-nav">
          <NavLink className="sales-nav__link" exact to="/payments">
            {lang("Resumen", "Summary")}
          </NavLink>
          <NavLink className="sales-nav__link" to="/payments/transactions">
            {lang("Transacciones", "Transactions")}
          </NavLink>
          <NavLink className="sales-nav__link" to="/payments/payouts">
            {lang("Depósitos", "Payouts")}
          </NavLink>
          <NavLink className="sales-nav__link" to="/payments/links">
            {lang("Links de cobro", "Payment links")}
          </NavLink>
        </nav>

        {renderView()}
      </div>
    </main>
  );
};

export default Payments;
